import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  ArrowLeftIcon,
  CalendarIcon,
  ImageIcon,
  PlusIcon,
  ShoppingBag,
  UserIcon,
} from "@/components/icons";
import { Button, DatePicker, Input, Skeleton, Textarea, toast } from "@/components/kit";
import {
  FieldContent,
  FieldLabel,
  Field as FieldLayout,
} from "@/components/ui/field";
import type { CreateCustomJobDTO } from "@/modules/custom-jobs/domain/contracts";
import { calculateCustomJobTotals } from "@/modules/custom-jobs/domain/job-totals";
import { useCustomerSearch } from "@/modules/orders/presentation/api/queries";
import { ProductFormSectionLike } from "@/modules/orders/presentation/ui/OrderSection";
import {
  ProductPickerModal,
  type PickedVariant,
} from "@/modules/orders/presentation/ui/ProductPickerModal";
import { formatLak } from "@/shared/lib/currency";
import { formatDateForInput } from "@/shared/lib/date-time";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  useCreateCustomJob,
  useCustomJobQuery,
  useUpdateCustomJob,
} from "../api/queries";
import {
  CustomJobFormLines,
  CustomJobFormSummaryBar,
  type CustomJobFormLine,
} from "../ui/CustomJobFormLines";

type CustomJobFormPageProps = {
  jobId?: string;
};

function newKey() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function CustomJobFormPage({
  jobId: jobIdProp,
}: CustomJobFormPageProps) {
  const params = useParams({ strict: false });
  const routeJobId = typeof params.id === "string" ? params.id : undefined;
  const jobId = jobIdProp ?? routeJobId;
  const nav = useNavigate();
  const isEdit = Boolean(jobId);
  const jobQuery = useCustomJobQuery(jobId ?? "");
  const createJob = useCreateCustomJob();
  const updateJob = useUpdateCustomJob(jobId ?? "");

  const [hydrated, setHydrated] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [note, setNote] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [lines, setLines] = useState<CustomJobFormLine[]>([]);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const customers = useCustomerSearch(customerSearch);

  useEffect(() => {
    if (!isEdit || !jobQuery.data || hydrated) return;
    const job = jobQuery.data;
    setName(job.customerName);
    setPhone(job.customerPhone ?? "");
    setDueDate(job.dueDate ?? "");
    setNote(job.note ?? "");
    setDiscountAmount(job.discountAmount === "0.00" ? "" : job.discountAmount);
    setLines(
      job.items.map((item) => {
        if (item.lineType === "material" && item.variantId) {
          return {
            key: item.id,
            lineType: "material" as const,
            variantId: item.variantId,
            description: item.description,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
          };
        }
        return {
          key: item.id,
          lineType: item.lineType as "labor" | "other",
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        };
      }),
    );
    setAttachments(job.attachments.map((a) => a.imageKey));
    setHydrated(true);
  }, [isEdit, jobQuery.data, hydrated]);

  const subtotal = useMemo(
    () =>
      lines.reduce(
        (sum, line) => sum + Number(line.unitPrice || 0) * line.quantity,
        0,
      ),
    [lines],
  );

  const totals = useMemo(
    () => calculateCustomJobTotals(subtotal, Number(discountAmount || 0) || 0),
    [subtotal, discountAmount],
  );

  const addMaterial = (variant: PickedVariant) => {
    setLines((prev) => [
      ...prev,
      {
        key: newKey(),
        lineType: "material",
        variantId: variant.variantId,
        description: `${variant.productName} · ${variant.variantName}`,
        quantity: 1,
        unitPrice: variant.unitPrice,
      },
    ]);
    setPickerOpen(false);
  };

  const addManualLine = (lineType: "labor" | "other") => {
    setLines((prev) => [
      ...prev,
      {
        key: newKey(),
        lineType,
        description: "",
        quantity: 1,
        unitPrice: "",
      },
    ]);
  };

  const buildPayload = (): CreateCustomJobDTO => {
    const items = lines.map((line) => {
      const unitPrice = Number(line.unitPrice || 0);
      if (line.lineType === "material") {
        return {
          lineType: "material" as const,
          variantId: line.variantId,
          quantity: line.quantity,
          unitPrice,
        };
      }
      return {
        lineType: line.lineType,
        description: line.description.trim(),
        quantity: line.quantity,
        unitPrice,
      };
    });

    return {
      customer: { name: name.trim(), phone: phone.trim() || null },
      dueDate: dueDate || null,
      note: note.trim() || null,
      discountAmount: Number(discountAmount || 0) || 0,
      items,
      attachments: attachments
        .filter((k) => k.trim())
        .map((imageKey) => ({ imageKey })),
    };
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("ກະລຸນາໃສ່ຊື່ລູກຄ້າ");
      return;
    }
    if (lines.length === 0) {
      toast.error("ກະລຸນາເພີ່ມຢ່າງນ້ອຍ 1 ລາຍການ");
      return;
    }
    for (const line of lines) {
      if (line.lineType !== "material" && !line.description.trim()) {
        toast.error("ກະລຸນາໃສ່ຊື່ລາຍການ");
        return;
      }
    }

    try {
      const payload = buildPayload();
      if (isEdit) {
        const job = await updateJob.mutateAsync(payload);
        toast.success("ບັນທຶກສຳເລັດ");
        nav({ to: "/app/custom-jobs/$id", params: { id: job.id } });
      } else {
        const job = await createJob.mutateAsync(payload);
        toast.success("ສ້າງງານສຳເລັດ");
        nav({ to: "/app/custom-jobs/$id", params: { id: job.id } });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ບັນທຶກບໍ່ສຳເລັດ");
    }
  };

  if (isEdit && jobQuery.isLoading) {
    return (
      <>
        <Header />
        <Main className="flex flex-col gap-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-40 rounded-2xl" />
          <Skeleton className="h-64 rounded-2xl" />
        </Main>
      </>
    );
  }

  const isSubmitting = createJob.isPending || updateJob.isPending;

  return (
    <>
      <Header />
      <Main className="mx-auto flex w-full max-w-4xl flex-col gap-6 pb-32">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              {isEdit ? "ແກ້ໄຂງານສັ່ງເຮັດ" : "ສ້າງງານສັ່ງເຮັດ"}
            </h2>
            <p className="text-muted-foreground text-sm">
              {isEdit
                ? `${jobQuery.data?.jobNumber} · ແກ້ໄຂກ່ອນເລີ່ມເຮັດ`
                : "ກຳນົດລູກຄ້າ, ວັດຖຸດິບ ແລະ ຄ່າແຮງ"}
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <Button
            variant="outline"
            className="shrink-0 rounded-full border-secondary/40"
            onClick={() =>
              nav({
                to:
                  isEdit && jobId ? "/app/custom-jobs/$id" : "/app/custom-jobs",
                params: isEdit && jobId ? { id: jobId } : undefined,
              })
            }
          >
            <ArrowLeftIcon data-icon="inline-start" />
            ກັບ
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-5">
          <div className="flex flex-col gap-6 lg:col-span-2">
            <ProductFormSectionLike
              icon={UserIcon}
              title="ລູກຄ້າ"
              description="ຊື່ ແລະ ເບີຕິດຕໍ່"
            >
              <div className="grid gap-3">
                <FieldLayout>
                  <FieldLabel>ຊື່</FieldLabel>
                  <FieldContent>
                    <Input
                      className="rounded-full"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setCustomerSearch(e.target.value);
                      }}
                      placeholder="ຊື່ລູກຄ້າ"
                    />
                  </FieldContent>
                </FieldLayout>
                <FieldLayout>
                  <FieldLabel>ເບີ</FieldLabel>
                  <FieldContent>
                    <Input
                      className="rounded-full"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="020..."
                    />
                  </FieldContent>
                </FieldLayout>
              </div>
              {customers.data?.data?.length ? (
                <div className="flex flex-wrap gap-2 rounded-2xl bg-muted/25 p-3">
                  <p className="w-full text-muted-foreground text-xs">
                    ເລືອກດ່ວນ
                  </p>
                  {customers.data.data.slice(0, 5).map((c) => (
                    <Button
                      key={c.id}
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => {
                        setName(c.name);
                        setPhone(c.phone ?? "");
                      }}
                    >
                      {c.name}
                    </Button>
                  ))}
                </div>
              ) : null}
            </ProductFormSectionLike>

            <ProductFormSectionLike
              icon={CalendarIcon}
              title="ລາຍລະອຽດງານ"
              description="ວັນນັດ ແລະ ໝາຍເຫດ"
            >
              <div className="grid gap-3">
                <FieldLayout>
                  <FieldLabel>ວັນນັດສົ່ງ</FieldLabel>
                  <FieldContent>
                    <DatePicker
                      value={dueDate ? new Date(dueDate) : null}
                      onChange={(date) =>
                        setDueDate(date ? formatDateForInput(date) : "")
                      }
                      placeholder="ເລືອກວັນນັດສົ່ງ"
                      captionLayout="dropdown"
                      fromYear={2024}
                      toYear={new Date().getFullYear() + 2}
                      className="w-full rounded-full"
                    />
                  </FieldContent>
                </FieldLayout>
                <FieldLayout>
                  <FieldLabel>ລາຍລະອຽດ</FieldLabel>
                  <FieldContent>
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="ສີ, ຂະໜາດ, ແບບ..."
                      rows={3}
                      className="rounded-2xl"
                    />
                  </FieldContent>
                </FieldLayout>
              </div>
            </ProductFormSectionLike>

            <ProductFormSectionLike
              icon={ImageIcon}
              title="ຮູບອ້າງອິງ"
              description="ບໍ່ບັງຄັບ"
            >
              <div className="flex flex-col gap-3">
                {attachments.map((key, index) => (
                  <div
                    key={key || `attachment-empty-${index}`}
                    className="rounded-2xl border border-border/50 bg-muted/20 p-3"
                  >
                    <ImageKeyUploadField
                      keyPrefix="uploads/custom-jobs"
                      value={key}
                      onChange={(next) =>
                        setAttachments((prev) =>
                          prev.map((k, i) => (i === index ? next : k)),
                        )
                      }
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="mt-2 rounded-full text-destructive"
                      onClick={() =>
                        setAttachments((prev) =>
                          prev.filter((_, i) => i !== index),
                        )
                      }
                    >
                      ລຶບຮູບ
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => setAttachments((prev) => [...prev, ""])}
                >
                  <PlusIcon data-icon="inline-start" />
                  ເພີ່ມຮູບ
                </Button>
              </div>
            </ProductFormSectionLike>
          </div>

          <div className="flex flex-col gap-6 lg:col-span-3">
            <ProductFormSectionLike
              icon={ShoppingBag}
              title="ລາຍການ"
              description={`${lines.length} ລາຍການ · ${formatLak(totals.total)}`}
              action={
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => setPickerOpen(true)}
                  >
                    <PlusIcon data-icon="inline-start" />
                    ວັດຖຸດິບ
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => addManualLine("labor")}
                  >
                    ຄ່າແຮງ
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => addManualLine("other")}
                  >
                    ອື່ນໆ
                  </Button>
                </div>
              }
            >
              <CustomJobFormLines lines={lines} onChange={setLines} />

              <div className="mt-4 rounded-2xl border border-secondary/20 bg-secondary/5 p-4">
                <FieldLayout>
                  <FieldLabel>ສ່ວນລົດ (₭)</FieldLabel>
                  <FieldContent>
                    <Input
                      type="number"
                      min={0}
                      className="rounded-full bg-background"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(e.target.value)}
                    />
                  </FieldContent>
                </FieldLayout>
                <div className="mt-3 flex flex-col gap-1 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>ລາຍການ</span>
                    <span className="tabular-nums">
                      {formatLak(totals.subtotal)}
                    </span>
                  </div>
                  {totals.discountAmount > 0 ? (
                    <div className="flex justify-between text-muted-foreground">
                      <span>ສ່ວນລົດ</span>
                      <span className="text-destructive tabular-nums">
                        −{formatLak(totals.discountAmount)}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            </ProductFormSectionLike>
          </div>
        </div>
      </Main>

      <CustomJobFormSummaryBar
        subtotal={totals.subtotal}
        discountAmount={totals.discountAmount}
        total={totals.total}
        isSubmitting={isSubmitting}
        isEdit={isEdit}
        onSubmit={handleSubmit}
      />

      <ProductPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={addMaterial}
        excludeBundles
        subtitle="ເລືອກວັດຖຸດິບຈາກສະຕັອກ"
      />
    </>
  );
}
